import * as ExcelJS from 'exceljs';
import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import { BehaviorStrategyExport } from './admin-strategy';

@Injectable({
  providedIn: 'root',
})
export class BehaviorStrategyExcelService {
  /**
   * Export behavior strategies data to Excel
   * @param strategies The behavior strategies data array
   * @param fileName The name of the Excel file
   */

  async exportStrategiesToExcel(
    strategies: BehaviorStrategyExport[],
    fileName: string
  ): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Behavior Strategies');

    const columns = [
      { header: 'Title', key: 'title', width: 20 },
      { header: 'Description', key: 'description', width: 40 },
      { header: 'Is Setting Event', key: 'isSettingEvent', width: 15 },
      { header: 'Suitable For School', key: 'suitableForSchool', width: 15 },
      { header: 'Supervision Type', key: 'supervisionType', width: 30 },
      { header: 'Language Level', key: 'languageLevel', width: 15 },
      { header: 'Strategy Type', key: 'strategyType', width: 20 },
      { header: 'Guidelines', key: 'guidelines', width: 40 },
      { header: 'Detail Prompts', key: 'detailPrompts', width: 40 },
      { header: 'Settings', key: 'settings', width: 30 },
      { header: 'Antecedents', key: 'antecedents', width: 30 },
      { header: 'Environments', key: 'environments', width: 30 },
      { header: 'Consequences', key: 'consequences', width: 30 },
      { header: 'Functions', key: 'functions', width: 30 },
      { header: 'Age Levels', key: 'ageLevels', width: 20 },
      { header: 'Replacement Types', key: 'replacementTypes', width: 30 },
    ];

    worksheet.columns = columns;

    strategies.forEach(strategy => {
      worksheet.addRow({
        title: strategy.title,
        description: strategy.description,
        isSettingEvent: strategy.isSettingEvent ? 'Yes' : 'No',
        suitableForSchool: strategy.suitableForSchool ? 'Yes' : 'No',
        supervisionType: strategy.supervisionTypeName,
        languageLevel: strategy.languageLevelName,
        strategyType: strategy.behaviourStrategyTypeName,
        guidelines: strategy.guidelines?.map(g => g.guideline).join('\r\n\n'),
        detailPrompts: strategy.behaviorStrategyDetailPrompts
          ?.map(p => p.detailPrompt)
          .join('\r\n\n'),
        settings: strategy.behaviourStrategySettings
          ?.map(s => s.name)
          .join('\r\n\n'),
        antecedents: strategy.behaviourStrategyAntecedents
          ?.map(a => a.name)
          .join('\r\n\n'),
        environments: strategy.behaviourStrategyEnvironments
          ?.map(e => e.name)
          .join('\r\n\n'),
        consequences: strategy.behaviourStrategyConsequences
          ?.map(c => c.name)
          .join('\r\n\n'),
        functions: strategy.behaviourStrategyFunctions
          ?.map(f => f.name)
          .join('\r\n\n'),
        ageLevels: strategy.behaviourStrategyAgeLevels
          ?.map(a => a.name)
          .join('\r\n\n'),
        replacementTypes: strategy.behaviourStrategyReplacementBehaviourTypes
          ?.map(r => r.name)
          .join('\r\n\n'),
      });
    });

    worksheet.getRow(1).eachCell(cell => {
      cell.font = {
        bold: true,
        size: 10,
        color: { argb: '3b424f' },
        name: 'Inter',
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'DDF6EB' },
      };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    worksheet.eachRow((row, rowIndex) => {
      if (rowIndex !== 1) {
        row.eachCell(cell => {
          cell.font = { size: 10, color: { argb: '3b424f' }, name: 'Inter' };
          cell.alignment = {
            vertical: 'top',
            horizontal: 'left',
            wrapText: true,
          };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          };
        });
        row.height = 30;
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const data = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(data, `${fileName}.xlsx`);
  }
}
